import { FILE_INPUT_COUNT_MAX_FILES } from '@frontend/api-client';
import type { LocalFile, LocalOrSavedFile } from '@frontend/api-types';
import { identity, mergeAndDedupe } from '@frontend/duck-tape';
import { makeLocalFileList } from '@frontend/forms';
import { useFeatureIsOn, useState, useVisibilityController } from '@frontend/react';
import {
  ActionButton,
  DragAndDropModal,
  FilePreviewModal,
  Icon,
  LinkButton,
  XStack,
  YStack,
  mimeTypesMap,
  showToast,
} from '@frontend/web-react';
import type { Hotkey } from '@frontend/web-utils';
import { applyClass, concatClasses, getHotkeyHandler, safeCss, type ClassName } from '@frontend/web-utils';
import { useTestIds } from '@frontend/web/hooks/useTestIds';
import { FileButton as MFileButton, Textarea, type TextareaProps } from '@mantine/core';
import { FilePreview } from './FilePreview';

type ChatFooterProps = {
  autoFocus?: boolean;
  className?: ClassName;
  fileCountLimit?: number;
  filesValue: LocalFile[];
  isCompleteLoading?: boolean;
  isStreamResponseLoading?: boolean;
  isSubmitLoading?: boolean;
  muted?: boolean;
  onChangeFiles?: (files: LocalFile[]) => void;
  onChangeText: (text: string) => void;
  onClickInterrupt?: () => Promise<void> | void;
  onClickSubmit: () => Promise<void> | void;
  onComplete?: () => Promise<void> | void;
  placeholder?: string;
  submitDisabled?: boolean;
  submitShortcut?: Hotkey;
  textInputDisabled?: boolean;
  textInputRef?: React.RefObject<HTMLTextAreaElement>;
  textValue: string;
};

export const ChatFooter = ({
  autoFocus,
  className,
  fileCountLimit = FILE_INPUT_COUNT_MAX_FILES,
  filesValue,
  isCompleteLoading,
  isStreamResponseLoading,
  isSubmitLoading,
  muted,
  onChangeFiles,
  onChangeText,
  onClickInterrupt,
  onClickSubmit,
  onComplete,
  placeholder = 'Enter a message...',
  submitDisabled,
  submitShortcut,
  textInputDisabled,
  textInputRef,
  textValue,
}: ChatFooterProps) => {
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');
  const [isPreviewFileModalOpen, { close: closeFilePreviewModal, open: openFilePreviewModal }] =
    useVisibilityController();

  const [filePreviewModalSelectedFile, setFilePreviewModalSelectedFile] = useState<LocalOrSavedFile>();

  const testIds = useTestIds(['/app/taskId.sendMessageButton']);

  const onRemoveFile = (file: LocalFile) => {
    onChangeFiles?.(filesValue?.filter(({ name }) => name !== file.name));
  };

  const onChangeComposed = (newFiles: LocalFile[]) => {
    const dedupedFiles = mergeAndDedupe(filesValue, newFiles, ({ name }) => name);
    if (dedupedFiles.length > fileCountLimit) {
      onChangeFiles?.(dedupedFiles.slice(0, fileCountLimit));
      showToast({
        message: `You can only attach up to ${fileCountLimit} files. The extra files were not included.`,
        title: 'File limit reached',
        variant: 'warning',
      });
    } else {
      onChangeFiles?.(dedupedFiles);
    }
  };

  const onClickFilePreview = (file: LocalOrSavedFile) => {
    setFilePreviewModalSelectedFile(file);
    openFilePreviewModal();
  };

  const textareaInputClassNames: TextareaProps['classNames'] = {
    input: safeCss(
      'py-md px-none text-p-md outline-hidden text-textPrimary placeholder:text-textTertiary border-none bg-trueWhite',
    ),
    root: safeCss('gap-y-xs flex flex-col flex-1 w-full'),
  };

  const fileLimitReached = fileCountLimit <= filesValue.length;

  if (isGameplanPricingEnabled) {
    return (
      <YStack
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={concatClasses(
          'border-[0.5px] border-borderPrimary shadow-five px-md pt-sm pb-sm rounded-lg gap-sm bg-trueWhite',
          applyClass(muted, 'opacity-60'),
          className,
        )}
      >
        <DragAndDropModal fileLimitReached={fileLimitReached} onFilesDropped={onChangeComposed} />
        {filesValue.length ? (
          <XStack className="gap-sm flex-wrap pt-sm">
            {filesValue?.map((file) => (
              <FilePreview
                file={file}
                key={file.name}
                onClick={() => onClickFilePreview(file)}
                onRemove={() => onRemoveFile(file)}
              />
            ))}
          </XStack>
        ) : null}
        <Textarea
          autoFocus={autoFocus}
          autosize
          classNames={textareaInputClassNames}
          disabled={textInputDisabled}
          maxRows={10}
          minRows={1}
          name="message"
          onChange={(event) => onChangeText(event.currentTarget.value)}
          onKeyDown={
            submitShortcut ? getHotkeyHandler([[submitShortcut, submitDisabled ? identity : onClickSubmit]]) : undefined
          }
          placeholder={placeholder}
          ref={textInputRef}
          value={textValue}
        />
        <XStack className="flex gap-sm" onClick={() => textInputRef?.current?.focus()}>
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <XStack className="grow-1 gap-[12px]">
            {onChangeFiles ? (
              // We could add more file types here if we want to support more file types
              <MFileButton
                accept={Object.keys(mimeTypesMap).join(',')}
                multiple
                onChange={(files) => onChangeComposed(makeLocalFileList(files))}
              >
                {(buttonProps) => <LinkButton label="" leftIconName="IconPaperclip" type="h6" {...buttonProps} />}
              </MFileButton>
            ) : null}
            {onComplete ? (
              <ActionButton
                isLoading={isCompleteLoading}
                label="Complete"
                leftIconName="IconCheck"
                onClick={onComplete}
                variant="filled"
              />
            ) : null}
          </XStack>
          <XStack alignItems="center">
            <Icon
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className={concatClasses(
                'rounded-[32px] border-1 border-[#77DFC4] bg-neutral-950 transition-opacity duration-150',
                applyClass(!submitDisabled, 'hover:bg-neutral-950  active:opacity-80 focus:opacity-90'),
                applyClass(!!submitDisabled, 'opacity-60 pointer-events-none cursor-not-allowed'),
              )}
              color="white"
              disabled={submitDisabled}
              enableBackgroundColor={false}
              isLoading={isSubmitLoading}
              name={isStreamResponseLoading && onClickInterrupt ? 'IconPlayerPauseFilled' : 'IconArrowUp'}
              onClick={isStreamResponseLoading && onClickInterrupt ? onClickInterrupt : onClickSubmit}
              rounded
              testId={testIds['/app/taskId.sendMessageButton']}
            />
          </XStack>
        </XStack>
        <FilePreviewModal
          file={filePreviewModalSelectedFile}
          isOpen={isPreviewFileModalOpen}
          onClose={closeFilePreviewModal}
          title={
            filePreviewModalSelectedFile?.__typename === 'LocalFile'
              ? filePreviewModalSelectedFile.name
              : filePreviewModalSelectedFile?.key
          }
        />
      </YStack>
    );
  }

  return (
    <YStack
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={concatClasses(
        'border-borderPrimary border-1 px-md pt-sm pb-sm rounded-lg gap-sm bg-trueWhite',
        applyClass(muted, 'opacity-60'),
        className,
      )}
    >
      <DragAndDropModal fileLimitReached={fileLimitReached} onFilesDropped={onChangeComposed} />
      {filesValue.length ? (
        <XStack className="gap-sm flex-wrap pt-sm">
          {filesValue?.map((file) => (
            <FilePreview
              file={file}
              key={file.name}
              onClick={() => onClickFilePreview(file)}
              onRemove={() => onRemoveFile(file)}
            />
          ))}
        </XStack>
      ) : null}
      <Textarea
        autoFocus={autoFocus}
        autosize
        classNames={textareaInputClassNames}
        disabled={textInputDisabled}
        maxRows={10}
        minRows={1}
        name="message"
        onChange={(event) => onChangeText(event.currentTarget.value)}
        onKeyDown={
          submitShortcut ? getHotkeyHandler([[submitShortcut, submitDisabled ? identity : onClickSubmit]]) : undefined
        }
        placeholder={placeholder}
        ref={textInputRef}
        value={textValue}
      />
      <XStack className="flex gap-sm" onClick={() => textInputRef?.current?.focus()}>
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <XStack className="grow-1 gap-[12px]">
          {onChangeFiles ? (
            // We could add more file types here if we want to support more file types
            <MFileButton
              accept={Object.keys(mimeTypesMap).join(',')}
              multiple
              onChange={(files) => onChangeComposed(makeLocalFileList(files))}
            >
              {(buttonProps) => <LinkButton label="Attach" leftIconName="IconPaperclip" type="h6" {...buttonProps} />}
            </MFileButton>
          ) : null}
          {onComplete ? (
            <ActionButton
              isLoading={isCompleteLoading}
              label="Complete"
              leftIconName="IconCheck"
              onClick={onComplete}
              variant="filled"
            />
          ) : null}
        </XStack>
        <XStack alignItems="center">
          <Icon
            color={isStreamResponseLoading && onClickInterrupt ? 'secondary' : 'primary'}
            disabled={submitDisabled}
            enableBackgroundColor
            isLoading={isSubmitLoading}
            name={isStreamResponseLoading && onClickInterrupt ? 'IconPlayerPauseFilled' : 'IconArrowUp'}
            onClick={isStreamResponseLoading && onClickInterrupt ? onClickInterrupt : onClickSubmit}
            rounded
            testId={testIds['/app/taskId.sendMessageButton']}
          />
        </XStack>
      </XStack>
      <FilePreviewModal
        file={filePreviewModalSelectedFile}
        isOpen={isPreviewFileModalOpen}
        onClose={closeFilePreviewModal}
        title={
          filePreviewModalSelectedFile?.__typename === 'LocalFile'
            ? filePreviewModalSelectedFile.name
            : filePreviewModalSelectedFile?.key
        }
      />
    </YStack>
  );
};
